import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import Container from "@mui/material/Container";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import apis from "../../services";

const formatDecimal3 = (value) => {
  if (value === null || value === undefined || value === "") return "0.000";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value);

  const truncatedValue = Math.trunc(numericValue * 1000) / 1000;
  return truncatedValue.toFixed(3);
};

const formatMoney3 = (value) => `$${formatDecimal3(value)} MXN`;

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const SectionCard = ({ title, children }) => (
  <Card
    sx={{
      borderRadius: 4,
      border: "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
      overflow: "hidden",
      mb: 3,
    }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
        background: "linear-gradient(135deg, rgba(230,14,16,0.05) 0%, rgba(255,107,74,0.05) 100%)",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#E60E10", letterSpacing: 0.3 }}>
        {title}
      </Typography>
    </Box>
    <CardContent sx={{ p: 3 }}>{children}</CardContent>
  </Card>
);

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Box
      sx={{
        height: "100%",
        p: 1.75,
        borderRadius: 3,
        backgroundColor: "rgba(15, 23, 42, 0.025)",
        border: "1px solid rgba(15, 23, 42, 0.05)",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, wordBreak: "break-word" }}>
        {value === null || value === undefined || value === "" ? "N/A" : String(value)}
      </Typography>
    </Box>
  </Grid>
);

export const ShareholderDetail = () => {
  const { id } = useParams();
  const [shareholder, setShareholder] = useState(null);
  const [lotHoldings, setLotHoldings] = useState([]);
  const [showShareIds, setShowShareIds] = useState(false);
  const [investorShares, setInvestorShares] = useState([]);
  const [sharePage, setSharePage] = useState(1);
  const [sharePages, setSharePages] = useState(1);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [res, holdingsResponse] = await Promise.all([
        apis.getShareholderDetail(id),
        apis.getInvestorShareHoldings(id).catch(() => null),
      ]);
      if (res?.data?.status) {
        setShareholder(res?.data?.shareholder);
        setLotHoldings(holdingsResponse?.data?.holdings || []);
      } else {
        toast.error(res?.data?.message || "Unable to load shareholder");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load shareholder");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (!showShareIds) return undefined;
    let active = true;
    const loadShares = async () => {
      setSharesLoading(true);
      try {
        const response = await apis.getInvestorShares(id, { page: sharePage, limit: 100, status: "active" });
        if (!active) return;
        setInvestorShares(response?.data?.shares || []);
        setSharePages(Math.max(1, Number(response?.data?.pagination?.totalPages || 1)));
      } catch (error) {
        if (active) toast.error(error?.message || "Unable to load permanent share IDs");
      } finally {
        if (active) setSharesLoading(false);
      }
    };
    loadShares();
    return () => { active = false; };
  }, [id, sharePage, showShareIds]);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await apis.exportShareholderPdf(id);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      toast.error(error?.message || "Unable to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box
          component="main"
          sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
          className="custom-scroll"
        >
          <Header />
          {loading || !shareholder ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <Container sx={{ mt: 4, mb: 4 }} className="mainContainerComp">
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2.5,
                }}
              >
                <Box>
                  <h3 className="dashboard-title" style={{ marginBottom: 6 }}>
                    {shareholder.personal.name}
                  </h3>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {shareholder.personal.email}
                  </Typography>
                </Box>
                <Button variant="danger" onClick={handleExportPdf} disabled={exportingPdf}>
                  {exportingPdf ? "Generating..." : "Export PDF"}
                </Button>
              </Box>

              <SectionCard title="1. Personal Information">
                <Grid container spacing={2}>
                  <Field label="Shareholder ID" value={shareholder.personal.shareholderId} />
                  <Field label="Name" value={shareholder.personal.name} />
                  <Field label="RFC" value={shareholder.personal.rfc} />
                  <Field label="CURP" value={shareholder.personal.curp} />
                  <Field label="Date of Birth" value={formatDate(shareholder.personal.dateOfBirth)} />
                  <Field label="Nationality" value={shareholder.personal.nationality} />
                  <Field label="Address" value={shareholder.personal.address} />
                  <Field label="Phone" value={shareholder.personal.phone} />
                  <Field label="Email" value={shareholder.personal.email} />
                </Grid>
              </SectionCard>

              <SectionCard title="2. Capital">
                <Grid container spacing={2}>
                  <Field label="Total Shares" value={formatDecimal3(shareholder.capital.totalShares)} />
                  <Field label="Series G Shares" value={formatDecimal3(shareholder.capital.seriesGShares)} />
                  <Field label="Series F Shares" value={formatDecimal3(shareholder.capital.seriesFShares)} />
                  <Field
                    label="Average Purchase Price"
                    value={
                      shareholder.capital.averagePurchasePrice !== null
                        ? formatMoney3(shareholder.capital.averagePurchasePrice)
                        : "N/A"
                    }
                  />
                  <Field label="Total Invested" value={formatMoney3(shareholder.capital.totalContributed)} />
                  <Field label="Current Dividends" value={formatMoney3(shareholder.capital.currentDividends)} />
                  <Field label="Portfolio Value" value={formatMoney3(shareholder.capital.portfolioValue)} />
                  <Field label="Date of Investment" value={formatDate(shareholder.capital.dateOfInvestment)} />
                </Grid>
              </SectionCard>

              <SectionCard title="3. Permanent Share IDs by Lot">
                {lotHoldings.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No finalized permanent folios are recorded for this shareholder yet.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Lot</TableCell><TableCell>Shares</TableCell><TableCell>First folio</TableCell><TableCell>Last folio</TableCell><TableCell>Series / class</TableCell><TableCell>Original value</TableCell></TableRow></TableHead>
                      <TableBody>
                        {lotHoldings.map((holding) => (
                          <TableRow key={holding.lotId || holding.lotCode}>
                            <TableCell><strong>{holding.lotCode}</strong></TableCell>
                            <TableCell>{holding.quantity}</TableCell>
                            <TableCell>{holding.firstFolio}</TableCell>
                            <TableCell>{holding.lastFolio}</TableCell>
                            <TableCell>{holding.legalSeries} / {holding.legalClass}</TableCell>
                            <TableCell>${Number(holding.originalValue || 0).toFixed(2)} MXN</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {lotHoldings.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button variant="outline-danger" onClick={() => setShowShareIds((value) => !value)}>
                      {showShareIds ? "Hide individual share IDs" : "View individual share IDs"}
                    </Button>
                    {showShareIds && (
                      <>
                        {sharesLoading ? (
                          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>Loading permanent share IDs...</Typography>
                        ) : (
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 1, mt: 2, maxHeight: 360, overflow: "auto" }}>
                            {investorShares.map((share) => (
                              <Box key={share._id || share.folio} sx={{ p: 1.25, border: "1px solid rgba(15,23,42,.09)", borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>{share.folio}</Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>{share.lotCode} · ${Number(share.originalPrice || 0).toFixed(2)} MXN</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, mt: 2 }}>
                          <Button variant="outline-secondary" disabled={sharePage <= 1 || sharesLoading} onClick={() => setSharePage((page) => page - 1)}>Previous</Button>
                          <Typography variant="caption">Page {sharePage} of {sharePages}</Typography>
                          <Button variant="outline-secondary" disabled={sharePage >= sharePages || sharesLoading} onClick={() => setSharePage((page) => page + 1)}>Next</Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </SectionCard>

              <SectionCard title="4. Transactions">
                {shareholder.transactions.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No transactions on record.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Shares</TableCell>
                          <TableCell>Lot</TableCell>
                          <TableCell>Permanent folios</TableCell>
                          <TableCell>Share Price</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Received</TableCell>
                          <TableCell>To Dividends</TableCell>
                          <TableCell>Transaction ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shareholder.transactions.map((t) => (
                          <TableRow key={t._id}>
                            <TableCell>{formatDate(t.purchasedAt)}</TableCell>
                            <TableCell>{t.type}</TableCell>
                            <TableCell>{formatDecimal3(t.purchasedShare)}</TableCell>
                            <TableCell>
                              {(t.lotAllocations || []).map((allocation) => allocation.lotCode).filter(Boolean).join(", ") || "N/A"}
                            </TableCell>
                            <TableCell sx={{ minWidth: 260 }}>
                              {(t.lotAllocations || []).map((allocation) =>
                                allocation.firstFolio && allocation.lastFolio
                                  ? `${allocation.firstFolio} - ${allocation.lastFolio}`
                                  : "Assigned after confirmation"
                              ).join(", ") || "N/A"}
                            </TableCell>
                            <TableCell>{formatMoney3(t.sharePrice)}</TableCell>
                            <TableCell>{formatMoney3(t.amountInvested)}</TableCell>
                            <TableCell>{formatMoney3(t.amountReceived ?? t.amountInvested)}</TableCell>
                            <TableCell>{formatMoney3(t.dividendRemainder || 0)}</TableCell>
                            <TableCell>{t.transactionId || "N/A"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </SectionCard>

              <SectionCard title="5. Dividends">
                {shareholder.dividends.records.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No profit/dividend records on record.
                  </Typography>
                ) : (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Profit Amount</TableCell>
                          <TableCell>Loss</TableCell>
                          <TableCell>Running Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shareholder.dividends.records.map((d, index) => (
                          <TableRow key={index}>
                            <TableCell>{formatDate(d.date)}</TableCell>
                            <TableCell>{formatMoney3(d.profitAmount)}</TableCell>
                            <TableCell>{formatMoney3(d.loss)}</TableCell>
                            <TableCell>{formatMoney3(d.totalProfitAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Grid container spacing={2}>
                  <Field label="Total Profit" value={formatMoney3(shareholder.dividends.totalProfit)} />
                  <Field label="Total Loss" value={formatMoney3(shareholder.dividends.totalLoss)} />
                </Grid>
              </SectionCard>

              <SectionCard title="6. Compliance">
                <Grid container spacing={2}>
                  <Field label="KYC Status" value={shareholder.compliance.kycStatus} />
                  <Field label="Verified" value={shareholder.compliance.isVerified ? "Yes" : "No"} />
                </Grid>

                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#E60E10", textTransform: "uppercase", fontWeight: 800, letterSpacing: 0.5, mb: 1.5, display: "block" }}
                >
                  Documents
                </Typography>
                {shareholder.compliance.documents.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No documents on record.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {shareholder.compliance.documents.map((d, index) => (
                      <Chip
                        key={index}
                        label={`${d.type}: ${d.status}${d.rejectReason ? ` — ${d.rejectReason}` : ""}`}
                        color={d.status === "approved" ? "success" : d.status === "rejected" ? "error" : "warning"}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2.5 }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#E60E10", textTransform: "uppercase", fontWeight: 800, letterSpacing: 0.5, mb: 1.5, display: "block" }}
                >
                  Beneficial Owner(s)
                </Typography>
                {shareholder.compliance.beneficialOwners.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    None on record.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {shareholder.compliance.beneficialOwners.map((b, index) => (
                      <Field
                        key={index}
                        label={b.relationship || "Beneficiary"}
                        value={`${b.fullName || b.email}${b.percentage ? ` (${b.percentage}%)` : ""}`}
                      />
                    ))}
                  </Grid>
                )}
              </SectionCard>

              <SectionCard title="7. Legal">
                <Grid container spacing={2}>
                  <Field
                    label="Subscription Agreement"
                    value={shareholder.legal.subscriptionAgreementFile ? "On file" : "Not on file"}
                  />
                  <Field label="Agreement Date" value={formatDate(shareholder.legal.subscriptionAgreementDate)} />
                  <Field label="Terms Accepted" value={shareholder.legal.termsAccepted ? "Yes" : "No"} />
                  <Field label="Contract Signed (e-signature)" value={shareholder.legal.contractSigned ? "Yes" : "No"} />
                  <Field label="Version" value={shareholder.legal.version} />
                  <Field label="SHA-256 Hash" value={shareholder.legal.contractHash} />
                  <Field label="QR Verification" value={shareholder.legal.qrVerification} />
                </Grid>
              </SectionCard>

              <SectionCard title="8. Audit Trail">
                <Grid container spacing={2}>
                  <Field label="Username" value={shareholder.auditTrail.username} />
                  <Field label="Email" value={shareholder.auditTrail.email} />
                  <Field label="Category" value={shareholder.auditTrail.category} />
                  <Field label="Date of First Investment" value={formatDate(shareholder.auditTrail.dateOfFirstInvestment)} />
                  <Field label="Date of Last Investment" value={formatDate(shareholder.auditTrail.dateOfLastInvestment)} />
                  <Field label="Device Type" value={shareholder.auditTrail.deviceType} />
                </Grid>
              </SectionCard>
            </Container>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default ShareholderDetail;
